import { logger } from '@/utils/logger.js';
import { useTokchanStore } from '@/store/tokchanStore.js';

/**
 * 📋 Manager Registry - Phase 2改善
 * マネージャー間の初期化順序と依存関係を統一管理
 * Factory Patternの簡素版として、Registry Patternを採用
 */

class ManagerRegistry {
    static managers = new Map();
    static initOrder = ['transition', 'background', 'gift', 'mediapath']; // 依存関係順
    static linkAttempts = 0;
    static maxLinkAttempts = 10;
    static debugMode = false; // デバッグモード無効化
    static reactHookWaitList = new Map(); // React Hook初期化待ちリスト
    static mediaInitPromise = null; // MediaPathRegistry初期化プロミス
    static orchestratedMode = false; // 新しい初期化システムとの協調モード
    static orchestratedInitializationCompleted = false; // orchestrated初期化完了フラグ

    /**
     * マネージャーを登録
     * @param {string} name - マネージャー名 ('gift', 'background', 'transition')
     * @param {Object} instance - マネージャーインスタンス
     */
    static register(name, instance) {
        if (!instance) {
            logger.error(`📋 Cannot register null/undefined manager: ${name}`);
            return false;
        }

        this.managers.set(name, instance);

        if (this.debugMode) {
        } else {
        }

        // MediaPathRegistryの場合は初期化を確実に実行
        if (name === 'mediapath' && instance.initializeRegistry && typeof instance.initializeRegistry === 'function') {
            instance.initializeRegistry().catch(err => {
                logger.error('🎯 MediaPathRegistry initialization failed on registration:', err);
            });
        }

        // 登録後に自動リンクを試行（orchestratedMode でない場合のみ）
        if (!this.orchestratedMode) {
            this.tryAutoLink();
        } else {
            // orchestratedModeでも重要なマネージャーが登録された場合は再リンク試行
            if (['gift', 'background', 'transition'].includes(name)) {

                // 全ての重要マネージャーが揃ったかチェック
                const gift = this.managers.get('gift');
                const background = this.managers.get('background');
                const transition = this.managers.get('transition');

                if (gift && background && transition && !gift.managersLinked) {
                    const linkResult = this.tryAutoLink();
                    if (linkResult) {
                        logger.info('✅ Auto-relink successful for late-registered managers');
                    }
                }
            }
        }

        return true;
    }

    /**
     * マネージャーを取得
     * @param {string} name - マネージャー名
     * @returns {Object|null} マネージャーインスタンス
     */
    static get(name) {
        const manager = this.managers.get(name);
        if (!manager && this.debugMode) {
            logger.warn(`📋 Manager not found: ${name}`);
        }
        return manager;
    }

    /**
     * 自動的な依存関係設定
     * 全マネージャーが揃ったタイミングで自動リンク
     */
    static tryAutoLink() {
        this.linkAttempts++;

        const gift = this.managers.get('gift');
        const background = this.managers.get('background');
        const transition = this.managers.get('transition');
        const mediapath = this.managers.get('mediapath');

        // 必要なマネージャーが全て揃っているかチェック
        if (!gift || !background || !transition) {
            if (this.debugMode) {
            }

            // 最大試行回数に達したら警告
            if (this.linkAttempts >= this.maxLinkAttempts) {
                logger.warn(`📋 Auto-link failed after ${this.maxLinkAttempts} attempts`);
                logger.warn(`📋 Missing:`, {
                    gift: !gift,
                    background: !background,
                    transition: !transition,
                    mediapath: !mediapath
                });
            }
            return false;
        }

        // 既にリンク済みかチェック
        if (gift.managersLinked) {
            if (this.debugMode) {
                logger.info(`📋 Managers already linked, skipping`);
            }
            // MediaPath統合処理とReact Hook通知は続行
            this.handleMediaPathIntegration();
            this.notifyReactHooks();
            return true;
        }

        // 依存関係設定を実行
        try {
            // 🗑️ 旧GiftVideoManager → SimpleGiftSystemに移行済み
            // gift.setManagers(background, transition);
            // gift.managersLinked = true;

            // Background → Transition (相互参照)
            if (background.setTransitionManager && typeof background.setTransitionManager === 'function') {
                background.setTransitionManager(transition);
            } else if (!background.transitionManager) {
                background.transitionManager = transition;
            }

            // Transition → Background (相互参照)
            if (transition.setBackgroundManager && typeof transition.setBackgroundManager === 'function') {
                transition.setBackgroundManager(background);
            } else if (!transition.backgroundManager) {
                transition.backgroundManager = background;
            }


            // MediaPathRegistryとの統合処理
            this.handleMediaPathIntegration();

            // React Hook待機コンポーネントに通知
            this.notifyReactHooks();

            // デバッグ情報
            if (this.debugMode) {
                this.logLinkageStatus();
            }

            return true;

        } catch (error) {
            logger.error(`📋 Auto-link failed:`, error);
            return false;
        }
    }

    /**
     * MediaPathRegistryとの統合処理
     * React Hook初期化タイミング問題の根本解決
     */
    static handleMediaPathIntegration() {
        const mediapath = this.managers.get('mediapath');
        if (!mediapath) {
            if (this.debugMode) {
            }
            return false;
        }

        // MediaPathRegistryの初期化状況を確認
        const mediaStatus = mediapath.loadStatus;

        if (!mediaStatus) {
            logger.warn('🎯 MediaPathRegistry.loadStatus is undefined, triggering initialization');
            // 初期化を手動で実行
            if (mediapath.initializeRegistry && typeof mediapath.initializeRegistry === 'function') {
                mediapath.initializeRegistry().catch(err => {
                    logger.error('🎯 Manual MediaPathRegistry initialization failed:', err);
                });
            }
            this.waitForMediaPathInit(mediapath);
            return false;
        }

        const allLoaded = Object.values(mediaStatus).every(loaded => loaded);

        if (allLoaded) {
            this.notifyReactHooks();
            return true;
        } else {
            if (this.debugMode) {
            }

            // MediaPath初期化完了を監視
            this.waitForMediaPathInit(mediapath);
            return false;
        }
    }

    /**
     * MediaPathRegistry初期化完了を待機
     */
    static async waitForMediaPathInit(mediapath) {
        if (this.mediaInitPromise) {
            return this.mediaInitPromise;
        }

        this.mediaInitPromise = new Promise((resolve) => {
            // 即座に初期チェック
            const initialStatus = mediapath.loadStatus;
            if (initialStatus) {
                const requiredKeys = ['bgm', 'effects', 'gifts', 'transitions', 'characters'];
                const allLoaded = requiredKeys.every(key => initialStatus[key] === true);
                if (allLoaded) {
                    logger.info('🎯 MediaPathRegistry already initialized');
                    this.notifyReactHooks();
                    resolve(true);
                    return;
                }
            }

            let checkCount = 0;
            let timeoutId;
            const checkInterval = setInterval(() => {
                const mediaStatus = mediapath.loadStatus;
                checkCount++;

                // デバッグ情報を追加（5秒ごとに表示）
                if (!mediaStatus) {
                    if (checkCount % 50 === 0) { // 5秒ごと
                        logger.warn('🎯 MediaPathRegistry.loadStatus is undefined');
                    }
                    return;
                }

                if (checkCount % 30 === 0) { // 3秒ごとにログ出力  
                    const requiredKeys = ['bgm', 'effects', 'gifts', 'transitions', 'characters'];
                    const loadedKeys = requiredKeys.filter(key => mediaStatus[key] === true);
                    const pendingKeys = requiredKeys.filter(key => mediaStatus[key] !== true);
                    logger.info(`🎯 MediaPathRegistry status: ${loadedKeys.length}/${requiredKeys.length} loaded`);
                    if (pendingKeys.length > 0) {
                        logger.warn(`🎯 Pending keys: ${pendingKeys.map((key: string) =>
                            `${key}=${mediaStatus[key]} (${typeof mediaStatus[key]})`
                        ).join(', ')}`);
                    }
                }

                // unified は失敗してもOK、他がすべて読み込まれていればよい
                const requiredKeys = ['bgm', 'effects', 'gifts', 'transitions', 'characters'];
                const allLoaded = requiredKeys.every(key => {
                    const loaded = mediaStatus[key] === true;
                    if (!loaded && checkCount % 50 === 0) {
                    }
                    return loaded;
                });

                if (allLoaded) {
                    clearInterval(checkInterval);
                    clearTimeout(timeoutId); // タイムアウトタイマーもクリア
                    this.notifyReactHooks();
                    resolve(true);
                }
            }, 100);

            // 15秒でタイムアウト（デバッグ時間を確保）
            timeoutId = setTimeout(() => {
                clearInterval(checkInterval);
                const finalStatus = mediapath.loadStatus;
                const requiredKeys = ['bgm', 'effects', 'gifts', 'transitions', 'characters'];
                const loadedKeys = requiredKeys.filter(key => finalStatus[key] === true);
                const pendingKeys = requiredKeys.filter(key => finalStatus[key] !== true);

                logger.warn(`🎯 MediaPathRegistry initialization timeout after 15s`);
                logger.warn(`🎯 Loaded: ${loadedKeys.length}/${requiredKeys.length} - ${loadedKeys.join(', ')}`);
                if (pendingKeys.length > 0) {
                    logger.warn(`🎯 Failed to load: ${pendingKeys.join(', ')}`);
                }
                logger.warn(`🎯 Final status:`, finalStatus);

                this.notifyReactHooks(); // タイムアウトしても通知
                resolve(false);
            }, 15000);
        });

        return this.mediaInitPromise;
    }

    /**
     * React Hook待機リストへの追加
     * useLikeChargeEngine等のReact Hookがマネージャー初期化を待つ際に使用
     */
    static waitForInit(hookId, callback) {
        if (typeof callback !== 'function') {
            logger.error(`📋 Invalid callback for React Hook: ${hookId}`);
            return false;
        }

        this.reactHookWaitList.set(hookId, {
            callback,
            timestamp: Date.now()
        });

        if (this.debugMode) {
        }

        // 既に初期化済みの場合は即座にコールバック実行
        const gift = this.managers.get('gift');
        const mediapath = this.managers.get('mediapath');

        if (this.debugMode) {
            if (mediapath) {
                const mediaStatus = mediapath.loadStatus;
                logger.info(`  - all loaded: ${Object.values(mediaStatus).every(loaded => loaded)}`);
            }
        }

        // MediaPathRegistry統合チェック
        const mediaRegistry = mediapath;
        const managersReady = gift?.managersLinked;

        if (managersReady && mediaRegistry) {
            const mediaStatus = mediaRegistry.loadStatus;
            const allLoaded = mediaStatus && Object.values(mediaStatus).every(loaded => loaded);

            if (allLoaded) {
                logger.info(`📋 Immediate callback for ${hookId} (managers linked & media loaded)`);
                setTimeout(() => callback(), 0);
                return true;
            } else if (this.debugMode) {
                logger.info(`📋 Media not fully loaded yet:`, mediaStatus);
            }
        } else if (this.debugMode) {
        }

        return false;
    }

    /**
     * React Hook待機コンポーネントへの通知
     */
    static notifyReactHooks() {
        if (this.reactHookWaitList.size === 0) {
            if (this.debugMode) {
            }
            return;
        }


        const notifications = [];

        for (const [hookId, hookData] of this.reactHookWaitList.entries()) {
            try {

                // 即座にコールバック実行
                try {
                    hookData.callback();
                } catch (error) {
                    logger.error(`📋 React Hook callback error (${hookId}):`, error);
                }

                notifications.push(hookId);

            } catch (error) {
                logger.error(`📋 Error notifying React Hook ${hookId}:`, error);
            }
        }

        // 通知完了後にリストクリア
        notifications.forEach(hookId => {
            this.reactHookWaitList.delete(hookId);
        });

    }

    /**
     * 手動でReact Hook通知を実行（デバッグ用）
     */
    static forceNotifyReactHooks() {
        this.notifyReactHooks();
    }

    /**
     * 手動でのリンク実行
     * 自動リンクが失敗した場合の手動実行用
     */
    static forceLink() {
        this.linkAttempts = 0; // リセット
        return this.tryAutoLink();
    }

    /**
     * Orchestrated モードの設定
     * 新しい初期化システムとの協調モード
     */
    static setOrchestratedMode(enabled) {
        this.orchestratedMode = enabled;

        if (enabled) {
            this.orchestratedInitializationCompleted = false;
        }
    }

    /**
     * Orchestrated 初期化完了の通知
     * 新しい初期化システムから呼ばれる
     */
    static completeOrchestratedInitialization() {
        if (!this.orchestratedMode) {
            logger.warn('📋 completeOrchestratedInitialization called but orchestrated mode is not enabled');
            return false;
        }


        // 強制リンクを実行
        const linkResult = this.forceLink();

        if (linkResult) {
            this.orchestratedInitializationCompleted = true;
        } else {
            logger.warn('⚠️ Orchestrated initialization completed but linking failed');
        }

        return linkResult;
    }

    /**
     * 全マネージャーの初期化状態をチェック
     */
    static checkInitializationStatus() {
        const status = {
            managersRegistered: this.managers.size,
            expectedManagers: this.initOrder.length,
            isComplete: this.managers.size >= this.initOrder.length,
            linkedStatus: null
        };

        const gift = this.managers.get('gift');
        if (gift) {
            status.linkedStatus = !!gift.managersLinked;
        }

        return status;
    }

    /**
     * リンク状態の詳細ログ
     */
    static logLinkageStatus() {
        console.group('📋 Manager Linkage Status');

        const gift = this.managers.get('gift');
        const background = this.managers.get('background');
        const transition = this.managers.get('transition');

        if (gift) {
        }

        if (background) {
        }

        if (transition) {
        }

        console.groupEnd();
    }

    /**
     * デバッグモードの切り替え
     */
    static setDebugMode(enabled) {
        this.debugMode = enabled;
    }

    /**
     * 全マネージャーのリセット
     * テスト用・デバッグ用
     */
    static reset() {
        logger.warn(`📋 RESET: Clearing all manager registrations`);
        this.managers.clear();
        this.linkAttempts = 0;
    }

    /**
     * システム状態レポート
     */
    static getSystemReport() {
        const mediapath = this.managers.get('mediapath');
        let mediaStatus = null;

        if (mediapath) {
            const loadStatus = mediapath.loadStatus;
            mediaStatus = {
                registered: true,
                allLoaded: Object.values(loadStatus).every(loaded => loaded),
                loadStatus: loadStatus
            };
        }

        return {
            registeredManagers: Array.from(this.managers.keys()),
            expectedManagers: this.initOrder,
            linkAttempts: this.linkAttempts,
            isLinked: this.managers.get('gift')?.managersLinked || false,
            debugMode: this.debugMode,
            reactHookWaitList: this.reactHookWaitList.size,
            mediaPathStatus: mediaStatus,
            mediaInitPromise: !!this.mediaInitPromise
        };
    }

    /**
     * 緊急診断機能
     * 初期化問題のトラブルシューティング用
     */
    static diagnose() {
        console.group('🔍 ManagerRegistry Diagnosis');

        // 1. 登録状況
        this.initOrder.forEach(name => {
            const manager = this.managers.get(name);
            if (manager) {
            }
        });

        // 2. リンク状況
        const gift = this.managers.get('gift');
        if (gift) {
        } else {
        }

        // 3. グローバル変数チェック

        // 4. MediaPathRegistry状況
        const mediapath = this.managers.get('mediapath');
        if (mediapath) {
            const loadStatus = mediapath.loadStatus;
            Object.entries(loadStatus).forEach(([key, loaded]) => {
                logger.info(`  ${key}: ${loaded ? '✅' : '❌'}`);
            });
            const allLoaded = Object.values(loadStatus).every(loaded => loaded);
        }

        // 5. React Hook状況
        if (this.reactHookWaitList.size > 0) {
            Array.from(this.reactHookWaitList.keys()).forEach(hookId => {
            });
        }

        // 6. 推奨対処法
        if (this.managers.size < this.initOrder.length) {
        }
        if (gift && !gift.managersLinked) {
        }
        if (this.linkAttempts >= this.maxLinkAttempts) {
        }
        if (mediapath && !Object.values(mediapath.loadStatus).every(loaded => loaded)) {
        }
        if (this.reactHookWaitList.size > 0) {
        }

        console.groupEnd();

        return this.getSystemReport();
    }
}

// グローバル公開とURL設定によるデバッグモード
if (typeof window !== 'undefined') {
    // Zustand storeに登録
    useTokchanStore.getState().setManagerRegistry(ManagerRegistry as unknown as ReturnType<typeof useTokchanStore.getState>['utilitySystems']['managerRegistry']);

    // URL パラメータでデバッグモード設定
    const params = new URLSearchParams(location.search);
    if (params.get('debug') === 'true' || params.get('registryDebug') === 'true') {
        ManagerRegistry.setDebugMode(true);
    }

    // デバッグ用グローバル関数
    window.managerDiagnose = () => ManagerRegistry.diagnose();
    window.managerStatus = () => ManagerRegistry.getSystemReport();
    window.managerForceLink = () => ManagerRegistry.forceLink();
    window.managerForceNotify = () => ManagerRegistry.forceNotifyReactHooks();

}

export default ManagerRegistry;
